import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Loader2, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { startCheckout, usePaymentReturn } from "@/lib/checkout";
import { useTalk } from "./TalkLayout";
import { ago } from "./TalkChats";

export default function TalkWallet() {
  const { reloadMe } = useTalk();
  const [shop, setShop] = useState(null);
  const [w, setW] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/talk/wallet").then((r) => setW(r.data)).catch(() => {});
  const status = usePaymentReturn(() => { load(); reloadMe(); });
  useEffect(() => { api.get("/talk/shop").then((r) => setShop(r.data)); load(); }, []);
  const buy = async (id) => { setBusy(id); await startCheckout(id); setBusy(""); };
  return (
    <div className="space-y-7" data-testid="talk-wallet">
      <div className="rounded-3xl p-6 bg-gradient-to-br from-[#FFE300]/20 via-[#FF2BD6]/10 to-transparent border border-[#FFE300]/25">
        <div className="mono text-[10px] uppercase tracking-widest text-white/50">Jixxeo coin balance</div>
        <div className="font-heading font-black text-5xl flex items-center gap-2 mt-1" data-testid="talk-wallet-balance"><Coins className="w-10 h-10 text-[#FFE300]" />{w?.coins ?? "—"}</div>
        {w && <div className="text-xs text-white/60 mt-2">Received {w.received} · Sent {w.sent} in tips & gifts</div>}
        {status === "checking" && <div className="text-xs mt-2 flex items-center gap-1 text-[#00F0FF]" data-testid="talk-payment-checking"><Loader2 className="w-3 h-3 animate-spin" /> Confirming payment…</div>}
        <Link to="/wallet" className="inline-block mt-3 text-xs underline text-white/60" data-testid="talk-cashout-link">Cash out in your Jixxeo Wallet →</Link>
      </div>
      <section>
        <h2 className="font-heading font-black text-lg mb-3">Coin shop</h2>
        <div className="grid grid-cols-2 gap-3">
          {(shop?.packages || []).map((p, i) => (
            <button key={p.id} onClick={() => buy(p.id)} disabled={!!busy} data-testid={`talk-pack-${p.id}`}
              className={`relative rounded-3xl p-4 text-left border transition-transform hover:-translate-y-0.5 ${i === 2 ? "border-[#FF2BD6] bg-[#FF2BD6]/10" : "border-white/10 bg-white/[0.04]"}`}>
              {i === 2 && <span className="absolute -top-2 right-3 bg-[#FF2BD6] text-black text-[9px] font-black uppercase px-2 py-0.5 rounded-full">Best value</span>}
              <div className="font-heading font-black text-2xl flex items-center gap-1"><Coins className="w-5 h-5 text-[#FFE300]" />{p.coins.toLocaleString()}</div>
              <div className="mono text-sm text-white/70 mt-1">{busy === p.id ? "Opening checkout…" : `$${p.price.toFixed(2)}`}</div>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-white/40 mt-2">Secure card checkout. Coins work across every Jixxeo app.</p>
      </section>
      <section>
        <h2 className="font-heading font-black text-lg mb-3">Gifts you can send</h2>
        <div className="flex flex-wrap gap-2">{(shop?.gifts || []).map((g) => <span key={g.id} className="rounded-full bg-white/5 px-3 py-1.5 text-sm">{g.emoji} {g.name} <span className="mono text-[#FFE300] text-xs">{g.price}</span></span>)}</div>
      </section>
      <section>
        <h2 className="font-heading font-black text-lg mb-3">Activity</h2>
        {!w?.history?.length ? <div className="text-sm text-white/40" data-testid="talk-wallet-empty">No tips or gifts yet.</div> : w.history.map((h) => (
          <div key={h.id} className="flex items-center gap-3 py-2.5 border-b border-white/5" data-testid="talk-wallet-row">
            <span className={`w-9 h-9 rounded-full flex items-center justify-center ${h.direction === "in" ? "bg-[#39FF14]/15 text-[#39FF14]" : "bg-white/10"}`}>{h.direction === "in" ? <ArrowDownLeft className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}</span>
            <div className="flex-1 text-sm">{h.direction === "in" ? "From" : "To"} <b>{h.other_name}</b>{h.gift_id ? ` · ${h.gift_id} gift` : " · tip"}<div className="text-[11px] text-white/40">{ago(h.created_at)}</div></div>
            <span className={`mono font-bold ${h.direction === "in" ? "text-[#39FF14]" : "text-white/70"}`}>{h.direction === "in" ? "+" : "−"}{h.coins}</span>
          </div>
        ))}
      </section>
    </div>
  );
}
